'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  edgeLambdaKeys,
  listThreatReports,
  type ThreatReportSummary,
} from '@/shared/cors-edge-lambda-api';
import { useCaseFolderName } from './useCaseFolderName';
import { useCellServiceSession } from './useCellServiceSession';

const IN_PROGRESS_POLL_MS = 5000;

/**
 * Every threat report the signed-in analyst generated for a case, newest first.
 *
 * Polls while any row is still generating so the work-product cards and the
 * report version switcher pick up completion without a refresh.
 *
 * Reports are stored under the case's folder segment (a UUID once the case has a
 * ``folder_token``), not its display name, so the list is requested with that
 * segment (TR-4714). It is ``null`` until the cases list loads; the server then
 * falls back to the display name, and the query refetches when the segment resolves.
 */
export function useThreatReportVersions(
  caseId: string | undefined | null,
): UseQueryResult<ThreatReportSummary[], Error> {
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const trimmedCaseId = caseId?.trim() || '';
  const caseFolderName = useCaseFolderName(trimmedCaseId);

  return useQuery<ThreatReportSummary[], Error>({
    queryKey: edgeLambdaKeys.threatReportVersions(cellId, trimmedCaseId, caseFolderName),
    queryFn: ({ signal }) =>
      listThreatReports({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: trimmedCaseId,
        caseFolderName,
        signal,
      }),
    enabled: enabled && Boolean(trimmedCaseId),
    refetchInterval: (query) =>
      query.state.data?.some((report) => report.status === 'inprogress')
        ? IN_PROGRESS_POLL_MS
        : false,
  });
}
